import moment from 'moment';
import pluralize from './pluralize';
import { addDays, differenceInWeeks, format, startOfWeek } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

export function monthYear(date) {
  return moment(date).format('MMMM YYYY');
}

export function timeDayMonth(date) {
  return moment(date).format('MMMM Do, h:mm a');
}

export function relative(date) {
  return moment(date).fromNow();
}

export function readableDayMonth(date) {
  return moment(date).format('MMMM D, YYYY');
}

export function shortMonthDayYear(date) {
  return moment(date).format('MM/D/YY');
}

export function unix(date) {
  return moment.unix(date);
}

export function daysFromNow(date) {
  return moment(date).diff(moment(), 'days');
}

export function withinOneHourOf(date, otherDate) {
  return moment(date).isAfter(moment(otherDate).subtract(1, 'hours'));
}

export function isAfter(date, otherDate) {
  return moment(date).isAfter(moment(otherDate));
}

export function timePeriod(date, otherDate) {
  const a = moment(date);
  const b = moment(otherDate);

  const years = b.diff(a, 'years', true);
  const decimalPart = years % 1;
  const yearInt = parseInt(years, 10);

  if (years < 1) {
    const months = b.diff(a, 'months', false);
    if (months == 0) {
      return 'less than a month';
    }
    return pluralize(months, 'month', 'months', true);
  }
  if (yearInt == years) {
    return pluralize(yearInt, ' year', ' years', true);
  }
  if (decimalPart != 0) {
    const months = b.diff(a, 'months', false) - yearInt * 12;
    return (
      pluralize(yearInt, ' year, ', ' years, ', true) + pluralize(months, 'month', 'months', true)
    );
  }
}

export function next6Months() {
  const options = [{ value: 'ASAP' }];
  Array(6)
    .fill(0)
    .map((_, b) => {
      const month = moment().add(b, 'months');
      options.push({ value: month.format('MMMM YYYY') });
    });
  return options;
}

export function withinAWeek(date) {
  return differenceInWeeks(new Date(), new Date(date)) < 1;
}

export const normalizeAbsoluteDate = (date) =>
  new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
const getDayObject = (date = new Date()) => {
  const [day, month, year] = format(date, 'dd MM yyyy').split(' ');

  return {
    day,
    month,
    year,
  };
};

export const utcHourToTimeZone = (utcHourString, timeZone) => {
  const { day, month, year } = getDayObject();

  // this generates a UTC date with the time given above
  const dateAtHour = new Date(`${year}-${month}-${day}T${utcHourString}Z`);
  return formatInTimeZone(dateAtHour, timeZone, 'h:mm a');
};

export const tzHourToUTC = (hourString, timeZone) => {
  const { day, month, year } = getDayObject();

  const [timeString, amPm] = hourString.split(' ');
  const [hours, minutes] = timeString.split(':');

  const hoursAsNumber = Number(hours);
  const hours24Format =
    amPm.toLowerCase() === 'pm' && hoursAsNumber < 12 ? hoursAsNumber + 12 : hoursAsNumber;

  const utcDate = zonedTimeToUtc(
    `${year}-${month}-${day}T${hours24Format.toString().padStart(2, '0')}:${minutes}:00`,
    timeZone,
  );
  return formatInTimeZone(utcDate, 'UTC', 'HH:mm');
};

export const weekDayName = (weekdayNumber) => {
  // takes first day of week and adds days to get the weekday name
  //  Even in Chile (where first day of week is Monday) it marks Sunday as the
  // first day of the week, so this should work wherever
  // So, when day is Sunday (0) -> returns Sunday
  // When day is Monday (1) -> adds 1 day to Sunday -> returns Monday
  // etc
  const firstDayOfWeek = startOfWeek(new Date());
  return format(addDays(firstDayOfWeek, weekdayNumber), 'eeee');
};

const DAYS_OF_WEEK_NAME_TO_NUMBER = {
  monday: '1',
  tuesday: '2',
  wednesday: '3',
  thursday: '4',
  friday: '5',
  saturday: '6',
  sunday: '0',
};
export const weekdayNameToNumber = (weekdayName) => {
  return DAYS_OF_WEEK_NAME_TO_NUMBER[weekdayName.toLowerCase()];
};
